<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add' }} {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit, invalid }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreateOrUpdate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <div v-for="(form, i) in diagnosisForm" :key="i" class="mb-2 pt-2" :style="{ borderTop: i > 0 ? '2px solid lightgray' : ''}">
              <b-row>
                <b-col cols="12" md="10">
                  <validation-provider #default="validationContext" name="ICD 10 Medical Code" rules="">
                    <b-form-group>
                      <template #label>
                        <span>ICD 10 Medical Code<span style="color: tomato">*</span></span>
                      </template>
                      
                      <medical-code-select
                        :value="form.medical_code_id"
                        @change="val => form.medical_code_id = val"
                      />

                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col cols="12" md="1" class="d-flex align-items-center">
                  <b-button
                    variant="no-outline"
                    class=""
                    type="button"
                    @click="onRemove(form, i)"
                  >
                    <feather-icon icon="TrashIcon" size="20" color="red" />
                  </b-button>
                </b-col>
              </b-row>
            </div>

            <b-button
              variant="no-outline"
              class="float-right"
              type="button"
              @click="onAddToForm"
            >
              <feather-icon icon="PlusCircleIcon" size="25" color="dodgerblue" />
            </b-button>

            <!-- Form Actions -->
            <div class="d-flex mt-5 pt-2" style="border-top: 2px solid lightgray">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="invalid || disableForm || diagnosisForm.length < 1"
              >
                {{ editingData ? 'Update' : 'Save' }} & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import MedicalCodeSelect from "@/@core/components/shared/admin/selects/MedicalCodeSelect.vue";

import { required } from '@validations';
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  medical_code_id: '',
}

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BModal,
    BAlert,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormDatepicker,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,

    MedicalCodeSelect,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    resource: {
      type: String,
      required: true
    },
    isAddActive: {
      type: Boolean,
      required: true,
    },
    editingData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    consultationData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    modalTitle: {
      type: String,
      default: 'Record'
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      diagnosisForm: [],
    }
  },
  computed: {
    isUpdate(){
      return !!this.editingData
    },
    disableForm(){
      return this.editingData ? !this.can(`${this.resource}:CanUpdate`) : !this.can(`${this.resource}:CanAddNew`)
    },
  },
  watch: {
    editingData: {
      handler(data){
        if (data){
          const { _id, medical_code_id } = data;
          this.diagnosisForm = [
            {
              _id,
              medical_code_id,
            }
          ]
        } else {
          this.diagnosisForm = [cloneDeep(rawData)]
        }
      }, 
      immediate: true,
      deep: true
    }
  },
  methods: {
    async onCreateOrUpdate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.isUpdate && !this.can(`${this.resource}:CanAddNew`)) {
          return
        }

        if (this.isUpdate && !this.can(`${this.resource}:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.patientDiagnosis.create({
          diagnosis: this.diagnosisForm.map(data => ({ consultation_id: this.consultationData._id, ...data }))
        });

        this.$emit('refetchData');
        this.$emit('update:is-add-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Diagnosis updated successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onRemove(data, index){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (data._id){
          if (!this.can(`${this.resource}:CanDelete`)) {
            return
          }
        }

        if (data._id){
          await this.useJwt().mainService.patientDiagnosis.delete(data._id);
          this.diagnosisForm = this.diagnosisForm.filter(opt => opt._id !== data._id);
          this.$emit('refetchData')

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success!',
              variant: 'success',
              icon: 'CheckCircleIcon',
              text: `Record added successfully`
            },
          });
        } else {
          this.diagnosisForm.splice(index, 1);
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    onAddToForm(){
      this.diagnosisForm.push(cloneDeep(rawData));
    },
    resetForm() {
      this.diagnosisForm = [cloneDeep(rawData)];
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
      this.$emit('update:editing-data', null);
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
